import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './PreviewFlat.module.css';
import TabButton from '../../UI/TabButton/TabButton';
import ScalePreview from '../../UI/ScalePreview/ScalePreview';
import Icon from '../../Icon';
import PreviewTour from '../PreviewTour/PreviewTour';
import { FlatCardData } from '../../CardsComponents/types';
import cn from 'classnames';

const YANDEX_MAP_API_URL = "https://api-maps.yandex.ru/2.1/?lang=ru_RU";
const TAB_TOUR = "Трехмерный тур";
const TAB_PLAN = "Планировка";
const TAB_VISUAL = "Визуализация";
const TAB_FLOOR = "На этаже";
const TAB_MAP = "На карте";
const ZOOM_MIN = 10;
const ZOOM_MAX = 200;
const MAP_ZOOM_MIN = 2;
const MAP_ZOOM_MAX = 20;

type PreviewFlatProps = {
  activeTab?: string;
  setIsTourOpen: (open: boolean) => void;
  flatData: FlatCardData;
};

type TabName = typeof TAB_TOUR | typeof TAB_PLAN | typeof TAB_VISUAL | typeof TAB_FLOOR | typeof TAB_MAP;

const normalizeValue = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const pickString = (...values: Array<unknown>): string => {
  for (const value of values) {
    const normalized = normalizeValue(value);
    if (normalized) {
      return normalized;
    }
  }
  return '';
};

const toMapZoom = (value: number) =>
  MAP_ZOOM_MIN + (value - ZOOM_MIN) * (MAP_ZOOM_MAX - MAP_ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN);

const PreviewFlat: React.FC<PreviewFlatProps> = ({ activeTab = TAB_PLAN, setIsTourOpen, flatData }) => {
  const flatDataAny = flatData as unknown as Record<string, unknown>;
  const preview3DTour = pickString(
    flatData.preview3DTour,
    flatDataAny.preview3dTour,
    flatDataAny.preview_3d_tour
  );
  const previewPlan = pickString(
    flatData.previewPlan,
    flatDataAny.preview_plan
  );
  const previewVisual = pickString(
    flatData.previewVisual,
    flatDataAny.preview_visual,
    flatData.imageSecond,
    flatDataAny.image_second
  );
  const previewFloor = pickString(
    flatData.previewFloor,
    flatDataAny.preview_floor
  );
  const tabs = useMemo<TabName[]>(() => {
    const availableTabs: TabName[] = [];
    if (preview3DTour) {
      availableTabs.push(TAB_TOUR);
    }
    if (previewPlan) {
      availableTabs.push(TAB_PLAN);
    }
    if (previewVisual) {
      availableTabs.push(TAB_VISUAL);
    }
    if (previewFloor) {
      availableTabs.push(TAB_FLOOR);
    }
    availableTabs.push(TAB_MAP);
    return availableTabs;
  }, [preview3DTour, previewPlan, previewVisual, previewFloor]);
  const imagePrimary = pickString(flatData.image);
  const imageSecondary = pickString(flatData.imageSecond, flatDataAny.image_second);
  const preferFloorOrMap = !imagePrimary && !imageSecondary;
  const defaultTab = useMemo(() => {
    if (preferFloorOrMap) {
      if (previewFloor) {
        return TAB_FLOOR;
      }
      return TAB_MAP;
    }
    if (tabs.includes(activeTab as TabName)) {
      return activeTab as TabName;
    }
    if (tabs.includes(TAB_PLAN)) {
      return TAB_PLAN;
    }
    return tabs[0] || TAB_MAP;
  }, [preferFloorOrMap, previewFloor, tabs, activeTab]);

  const [currentTab, setCurrentTab] = useState<TabName>(defaultTab);
  const [previousTab, setPreviousTab] = useState<TabName>(defaultTab);
  const [zoomLevel, setZoomLevel] = useState(160);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<any>(null);
  const wheelHandlerRef = useRef<((event: WheelEvent) => void) | null>(null);
  const centerCoordinates = useMemo(() => [42.887141, 47.624338], []);
  const tabImages = useMemo<Partial<Record<TabName, string>>>(() => ({
    [TAB_PLAN]: previewPlan,
    [TAB_VISUAL]: previewVisual,
    [TAB_FLOOR]: previewFloor
  }), [previewPlan, previewVisual, previewFloor]);

  useEffect(() => {
    const scriptExists = document.querySelector(`script[src="${YANDEX_MAP_API_URL}"]`);
    if (!scriptExists) {
      const script = document.createElement("script");
      script.src = YANDEX_MAP_API_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const isTourTab = currentTab === TAB_TOUR;
    document.body.style.overflow = isTourTab ? 'hidden' : '';
    setIsTourOpen(isTourTab);
    return () => {
      document.body.style.overflow = '';
    };
  }, [currentTab, setIsTourOpen]);

  useEffect(() => {
    if (!tabs.includes(currentTab)) {
      setCurrentTab(defaultTab);
      setPreviousTab(defaultTab);
    }
  }, [tabs, currentTab, defaultTab]);

  useEffect(() => {
    if (currentTab !== TAB_MAP || !mapRef.current || !window.ymaps) {
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
      return;
    }

    let cancelled = false;
    window.ymaps.ready(() => {
      if (cancelled || mapInstance.current || !mapRef.current) {
        return;
      }

      mapInstance.current = new window.ymaps.Map(mapRef.current, {
        center: centerCoordinates,
        zoom: toMapZoom(zoomLevel),
        controls: []
      });

      const handleMouseWheel = (event: WheelEvent) => {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -10 : 10;
        setZoomLevel(prevZoom => {
          const newZoom = prevZoom + delta;
          return Math.max(ZOOM_MIN, Math.min(newZoom, ZOOM_MAX));
        });
      };

      wheelHandlerRef.current = handleMouseWheel;
      mapRef.current.addEventListener('wheel', handleMouseWheel, { passive: false });
    });

    return () => {
      cancelled = true;
      if (mapRef.current && wheelHandlerRef.current) {
        mapRef.current.removeEventListener('wheel', wheelHandlerRef.current);
        wheelHandlerRef.current = null;
      }
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
    };
  }, [currentTab, centerCoordinates]);

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setZoom(toMapZoom(zoomLevel), {
        duration: 300,
        timingFunction: 'ease-in-out'
      });
    }
  }, [zoomLevel]);

  const handleTabClick = (tab: string) => {
    const tabName = tab as TabName;
    if (tabName !== TAB_TOUR) {
      setPreviousTab(tabName);
    }
    setCurrentTab(tabName);
  };

  const handleCloseTour = () => {
    setCurrentTab(previousTab);
  };

  const handleLocationClick = () => {
    if (mapInstance.current) {
      mapInstance.current.panTo(centerCoordinates, {
        duration: 500,
        timingFunction: 'ease-out'
      });
    }
  };

  const images: Record<string, string> = {
    "Трехмерный тур": flatData.image,
    "Планировка": previewPlan,
    "Визуализация": previewVisual,
    "На этаже": previewFloor
  };

  return (
    <div className={styles.plan}>
      <div className={styles.padding_wrapper}>
        <div className={cn(styles.wrapper, (currentTab === TAB_MAP || currentTab === TAB_FLOOR) && styles.no_padding)}>
          <div className={styles.plan_image}>
            {currentTab === TAB_MAP ? (
              <div ref={mapRef} className={styles.map_container}>
                <div className={styles.controls_bar_map}>
                  <div className={styles.location_wrapper} onClick={handleLocationClick}>
                    <Icon name='locationIcon' width='8' height='8' />
                  </div>
                  <ScalePreview zoom={zoomLevel} setZoom={setZoomLevel} />
                </div>
              </div>
            ) : currentTab === TAB_TOUR ? (
              <PreviewTour onClose={handleCloseTour} flat={flatData} />
            ) : (
              <img src={tabImages[currentTab] || ''} alt={currentTab} />
            )}
          </div>
      </div>
      </div>
      <div className={styles.tabs_container}>
        {tabs.map((tab, index) => (
          <React.Fragment key={index}>
            <TabButton isActive={currentTab === tab} onClick={() => handleTabClick(tab)} theme='light'>
              {tab}
            </TabButton>
            {tab === TAB_TOUR && <div className={styles.separator} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default PreviewFlat;
